<script setup>
import { computed, onMounted, ref } from 'vue';
import { formatTime } from '@chatwoot/utils';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import BarChart from 'shared/components/charts/BarChart.vue';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';

const store = useStore();

const since = ref(0);
const until = ref(0);
const hideInactive = ref(false);
const errorMessage = ref('');
const latestRefreshToken = ref(0);

const labels = useMapGetter('labels/getLabels');
const labelSummary = useMapGetter('summaryReports/getLabelSummaryReports');
const uiFlags = useMapGetter('summaryReports/getUIFlags');

const isFetching = computed(() => uiFlags.value?.isFetchingLabelSummaryReports);

const getMetrics = id =>
  (labelSummary.value || []).find(m => m.id === Number(id)) || {};

const fmtTime = v => (v ? formatTime(v) : '--');
const fmtNum = v =>
  v === null || v === undefined ? '--' : Number(v).toLocaleString();

const rows = computed(() => {
  const list = (labels.value || []).map(label => {
    const m = getMetrics(label.id);
    return {
      id: label.id,
      title: label.title,
      color: label.color || '#6b7280',
      conversations: Number(m.conversationsCount || 0),
      firstResponse: m.avgFirstResponseTime ?? null,
      resolutionTime: m.avgResolutionTime ?? null,
      replyTime: m.avgReplyTime ?? null,
      resolutionCount: Number(m.resolvedConversationsCount || 0),
    };
  });
  const filtered = hideInactive.value
    ? list.filter(r => r.conversations > 0)
    : list;
  return filtered.sort((a, b) => b.conversations - a.conversations);
});

const chartRows = computed(() => rows.value.slice(0, 10));
const hasData = computed(() => rows.value.some(r => r.conversations > 0));

const chartCollection = computed(() => ({
  labels: chartRows.value.map(r => r.title || '--'),
  datasets: [
    {
      label: 'Conversations',
      data: chartRows.value.map(r => r.conversations),
      backgroundColor: chartRows.value.map(r => r.color || '#1d75e5'),
      borderRadius: 6,
      categoryPercentage: 0.72,
      barPercentage: 0.9,
    },
  ],
}));

const chartOptions = {
  plugins: { legend: { display: false } },
  scales: {
    x: {
      ticks: { maxRotation: 0, minRotation: 0 },
      grid: { drawOnChartArea: false },
    },
    y: {
      beginAtZero: true,
      ticks: { callback: v => Number(v).toLocaleString() },
      grid: { drawOnChartArea: false },
    },
  },
};

const createRefreshToken = () => {
  latestRefreshToken.value += 1;
  return latestRefreshToken.value;
};

const isLatestRefresh = token => token === latestRefreshToken.value;

const fetchAllData = async () => {
  if (!since.value && !until.value) return;
  const refreshToken = createRefreshToken();
  errorMessage.value = '';

  const [labelsResult, labelSummaryResult] = await Promise.allSettled([
    store.dispatch('labels/get'),
    store.dispatch('summaryReports/fetchLabelSummaryReports', {
      since: since.value,
      until: until.value,
      businessHours: false,
      excludeIssueTime: false,
    }),
  ]);

  if (!isLatestRefresh(refreshToken)) return;

  const labelsFailed =
    labelsResult.status === 'rejected' || labelsResult.value === false;
  const summaryFailed =
    labelSummaryResult.status === 'rejected' ||
    labelSummaryResult.value === false;

  if (labelsFailed || summaryFailed) {
    errorMessage.value =
      'Failed to fetch labels overview report. Please try again.';
  }
};

const onFilterChange = ({ from, to }) => {
  since.value = from;
  until.value = to;
  fetchAllData();
};

const onDownload = () => {
  const fileName = generateFileName({ type: 'label', to: until.value });
  store.dispatch('downloadLabelReports', {
    from: since.value,
    to: until.value,
    fileName,
  });
};

onMounted(() => {
  store.dispatch('labels/get');
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="lr-page">
    <div class="lr-shell">
      <div class="lr-header">
        <div class="lr-header-left">
          <h1 class="lr-title">{{ $t('LABEL_REPORTS.HEADER') }}</h1>
        </div>
      </div>

      <div class="lr-toolbar">
        <CompactDateInboxFilters
          :show-add-filter="false"
          :show-inbox="false"
          @filter-change="onFilterChange"
        />
      </div>

      <div class="lr-content">
        <div v-if="errorMessage" class="lr-error">
          {{ errorMessage }}
        </div>

        <div class="lr-detail-header">
          <h2 class="lr-detail-title">Label performance</h2>
          <div class="lr-detail-actions">
            <label class="lr-switch">
              <span
                class="lr-switch-track"
                :class="{ 'lr-switch-track--on': hideInactive }"
              >
                <input
                  v-model="hideInactive"
                  type="checkbox"
                  class="lr-switch-input"
                />
                <span class="lr-switch-thumb" />
              </span>
              <span>Hide inactive labels</span>
            </label>
            <button class="lr-export-btn" type="button" @click="onDownload">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                />
              </svg>
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        <div class="lr-table-scroll">
          <table class="lr-tbl">
            <thead>
              <tr>
                <th class="lr-th lr-th--label lr-sticky">Label</th>
                <th class="lr-th lr-th--metric">Total chats</th>
                <th class="lr-th lr-th--metric">Avg. first response</th>
                <th class="lr-th lr-th--metric">Avg. resolution</th>
                <th class="lr-th lr-th--metric">Avg. reply time</th>
                <th class="lr-th lr-th--metric">Resolution count</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="isFetching">
                <td colspan="6" class="lr-td lr-td--empty">
                  <div class="lr-spinner" />
                </td>
              </tr>
              <tr v-else-if="!rows.length">
                <td colspan="6" class="lr-td lr-td--empty">No labels found.</td>
              </tr>
              <template v-else>
                <tr v-for="row in rows" :key="row.id" class="lr-row">
                  <td class="lr-td lr-td--label lr-sticky">
                    <div class="lr-label-cell">
                      <span
                        class="lr-label-dot"
                        :style="{ background: row.color }"
                      />
                      <span class="lr-label-title">{{ row.title }}</span>
                    </div>
                  </td>
                  <td class="lr-td lr-td--metric">
                    <div class="lr-cell-val">
                      {{ fmtNum(row.conversations) }}
                    </div>
                  </td>
                  <td class="lr-td lr-td--metric">
                    <div class="lr-cell-val">
                      {{ fmtTime(row.firstResponse) }}
                    </div>
                  </td>
                  <td class="lr-td lr-td--metric">
                    <div class="lr-cell-val">
                      {{ fmtTime(row.resolutionTime) }}
                    </div>
                  </td>
                  <td class="lr-td lr-td--metric">
                    <div class="lr-cell-val">{{ fmtTime(row.replyTime) }}</div>
                  </td>
                  <td class="lr-td lr-td--metric">
                    <div class="lr-cell-val">
                      {{ fmtNum(row.resolutionCount) }}
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <section class="lr-chart-panel">
          <div class="lr-chart-head">
            <h2 class="lr-chart-title">Conversations by label</h2>
            <p class="lr-chart-sub">Top 10 labels</p>
          </div>
          <div class="lr-chart-body">
            <woot-loading-state
              v-if="isFetching"
              :message="$t('REPORT.LOADING_CHART')"
              class="h-full"
            />
            <div v-else-if="!hasData" class="lr-chart-empty">
              No data available for the selected period.
            </div>
            <BarChart
              v-else
              :collection="chartCollection"
              :chart-options="chartOptions"
            />
          </div>
        </section>
      </div>
    </div>
  </div>
</template>
<!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->

<style scoped>
.lr-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}

.lr-shell {
  min-width: 650px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

.lr-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
}

.lr-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}

.lr-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}

.lr-toolbar {
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0 3px 10px -6px;
}

.lr-content {
  padding: 16px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.lr-error {
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

/* ── Detail header ── */
.lr-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.lr-detail-title {
  margin: 0;
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
  color: #111827;
}

.lr-detail-actions {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.lr-switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  user-select: none;
  cursor: pointer;
}

.lr-switch-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.lr-switch-track {
  position: relative;
  width: 27px;
  height: 20px;
  border-radius: 15px;
  background: rgb(221, 221, 221);
  cursor: pointer;
  transition: background 0.2s ease;
  flex-shrink: 0;
}

.lr-switch-track--on {
  background: #111827;
}

.lr-switch-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.1) 0 2px 2px 0;
  transition: left 0.2s ease-in-out;
}

.lr-switch-track--on .lr-switch-thumb {
  left: 10px;
}

.lr-export-btn {
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.lr-export-btn:hover {
  background: #f9fafb;
}

/* ── Table ── */
.lr-table-scroll {
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: #cfd4dc transparent;
}

.lr-tbl {
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  background: #ffffff;
}

.lr-th {
  padding: 10px;
  text-align: left;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 600;
  color: #111827 !important;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}

.lr-th--label {
  min-width: 180px;
  padding-left: 8px;
}
.lr-th--metric {
  min-width: 150px;
  text-align: center;
}

.lr-sticky {
  position: sticky;
  left: 0;
  z-index: 5;
  background: #ffffff;
  box-shadow: 8px 0 12px -12px rgba(0, 0, 0, 0.25);
}

.lr-th.lr-sticky {
  z-index: 6;
}

.lr-td {
  padding: 8px 10px;
  vertical-align: middle;
  border-bottom: 1px solid #f3f4f6;
  background: #ffffff;
}

.lr-td--label {
  min-width: 180px;
  padding: 0 8px 0 0;
}
.lr-td--metric {
  min-width: 150px;
}
.lr-td--empty {
  text-align: center;
  padding: 32px;
  color: #6b7280;
}

.lr-row:hover .lr-td {
  background: #fafafa;
}
.lr-row:hover .lr-sticky {
  background: #fafafa;
}

.lr-label-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding-left: 4px;
}

.lr-label-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.lr-label-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.lr-cell-val {
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}

.lr-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: lr-spin 0.8s linear infinite;
  margin: 0 auto;
}

@keyframes lr-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Chart ── */
.lr-chart-panel {
  border: 1px solid #d8dee5;
  border-radius: 4px;
  background: #ffffff;
  overflow: hidden;
}

.lr-chart-head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 12px 16px 8px;
  border-bottom: 1px solid #dfe3e8;
}

.lr-chart-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.lr-chart-sub {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
}

.lr-chart-body {
  height: 260px;
  padding: 10px 16px 16px;
}

.lr-chart-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}

/* ── Dark mode override ── */
::global(body.dark) .lr-page,
::global(body.dark) .lr-shell,
::global(body.dark) .lr-header,
::global(body.dark) .lr-toolbar,
::global(body.dark) .lr-tbl,
::global(body.dark) .lr-chart-panel,
::global(body.dark) .lr-content,
::global(body.dark) .lr-table-scroll,
::global(body.dark) .lr-td,
::global(body.dark) .lr-sticky,
::global(body.theme-mix) .lr-page,
::global(body.theme-mix) .lr-shell,
::global(body.theme-mix) .lr-header,
::global(body.theme-mix) .lr-toolbar,
::global(body.theme-mix) .lr-tbl,
::global(body.theme-mix) .lr-chart-panel,
::global(body.theme-mix) .lr-content,
::global(body.theme-mix) .lr-table-scroll,
::global(body.theme-mix) .lr-td,
::global(body.theme-mix) .lr-sticky {
  background: #ffffff !important;
  border-color: #e5e7eb !important;
  color: #111827 !important;
}

::global(body.dark) .lr-detail-title,
::global(body.dark) .lr-label-title,
::global(body.dark) .lr-cell-val,
::global(body.theme-mix) .lr-detail-title,
::global(body.theme-mix) .lr-label-title,
::global(body.theme-mix) .lr-cell-val {
  color: #111827 !important;
}

::global(body.dark) .lr-th,
::global(body.dark) .lr-th *,
::global(body.theme-mix) .lr-th,
::global(body.theme-mix) .lr-th * {
  color: #111827 !important;
}

@media (max-width: 1100px) {
  .lr-content {
    padding: 12px 16px 16px;
  }
  .lr-toolbar {
    padding: 10px 16px 8px;
  }
}
</style>
